import { CollapsibleFilters } from '../../components/common/CollapsibleFilters';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Input,
  Select,
  Option,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/joy';
import {
  Plus as Add,
  MagnifyingGlass as Search,
  Funnel as FilterList,
  CaretLineLeft as FirstPage,
  CaretLineRight as LastPage,
  CaretLeft as NavigateBefore,
  CaretRight as NavigateNext,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { apiAccountingClient } from '../../lib/apiAccounting';
import type { JournalEntry } from '../../types/accounting';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

export const JournalEntries: React.FC = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page: currentPage,
        page_size: pageSize === -1 ? 1000 : pageSize,
      };
      if (searchTerm) params.search = searchTerm;
      if (filterStatus) params.status = filterStatus;

      const response = await apiAccountingClient.getJournalEntries(params);
      setEntries(response.results);
      setTotalCount(response.count);
    } catch (err) {
      setError('Failed to load journal entries');
      console.error('Error fetching entries:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, [currentPage, pageSize, searchTerm, filterStatus]);

  // Reset to first page when filters change (but not pageSize, as that's handled in onChange)
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1.5, mb: 3 }}>
        <Box><Typography level="h1">Journal entries</Typography><Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>Review transactions, open an entry, or record a balanced debit and credit entry.</Typography></Box>
        <Button
          startDecorator={<Add />}
          onClick={() => navigate('/accounting/journal-entries/new')}
        >
          New Entry
        </Button>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <CollapsibleFilters><Card sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, p: 2 }}>
          <Input
            placeholder="Search entries..." aria-label="Search journal entries"
            startDecorator={<Search />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Select
            placeholder="Status"
            value={filterStatus}
            onChange={(_, value) => setFilterStatus(value || '')}
            startDecorator={<FilterList />}
            sx={{ minWidth: { xs: 0, sm: 150 } }}
          >
            <Option value="">All Status</Option>
            <Option value="DRAFT">Draft</Option>
            <Option value="POSTED">Posted</Option>
            <Option value="VOIDED">Voided</Option>
          </Select>

        </Box>
      </Card></CollapsibleFilters>

      {/* Entries Table */}
      <Card>
        <ResponsiveTable<JournalEntry>
          rows={entries}
          getRowKey={(e) => e.id}
          rowActions={(e) => (
            <Button size="sm" variant="plain" onClick={() => navigate(`/accounting/journal-entries/${e.id}`)}>View</Button>
          )}
          emptyState={<Typography level="body-sm" sx={{ textAlign: 'center', p: 4 }}>No journal entries found</Typography>}
          columns={[
            { key: 'entry_number', sortValue: (row) => row.entry_number, header: 'Entry #', mobileVariant: 'header', render: (e) => <Typography level="title-sm">{e.entry_number}</Typography> },
            { key: 'description', sortValue: (row) => row.description ?? row.memo, header: 'Description', mobileVariant: 'body', render: (e) => <Typography level="body-sm">{e.description || e.memo || '-'}</Typography> },
            { key: 'date', sortValue: (row) => row.entry_date ? new Date(row.entry_date) : null, header: 'Date', render: (e) => formatDate(e.entry_date) },
            { key: 'type', sortValue: (row) => row.entry_type, header: 'Type', render: (e) => <Chip size="sm" variant="soft" color="neutral">{e.entry_type}</Chip> },
            { key: 'source', sortValue: (row) => row.is_system_generated, header: 'Source', render: (e) => e.is_system_generated ? <Chip size="sm" variant="soft" color="primary">Auto</Chip> : <Chip size="sm" variant="soft">Manual</Chip> },
            { key: 'status', sortValue: (row) => row.status, header: 'Status', render: (e) => <Chip size="sm" variant="soft" color={e.status === 'POSTED' ? 'success' : e.status === 'DRAFT' ? 'warning' : 'danger'}>{e.status}</Chip> },
            { key: 'debits', header: 'Debits', render: (e) => <Typography level="body-sm" sx={{ fontWeight: 'md' }}>{formatCurrency(e.total_debits || 0)}</Typography> },
            { key: 'credits', header: 'Credits', render: (e) => <Typography level="body-sm" sx={{ fontWeight: 'md' }}>{formatCurrency(e.total_credits || 0)}</Typography> },
          ]}
        />

        {/* Pagination Controls */}
        {totalCount > 0 && (
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider'
          }}>
            {/* Page Size Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography level="body-sm">Show:</Typography>
              <Select
                size="sm"
                value={pageSize}
                onChange={(_, value) => {
                  if (value) {
                    setPageSize(value);
                    setCurrentPage(1);
                  }
                }}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={50}>50</Option>
                <Option value={100}>100</Option>
                <Option value={-1}>All</Option>
              </Select>
              <Typography level="body-sm" sx={{ ml: 2 }}>
                {pageSize === -1
                  ? `Showing all ${totalCount} entries`
                  : `Showing ${Math.min((currentPage - 1) * pageSize + 1, totalCount)}-${Math.min(currentPage * pageSize, totalCount)} of ${totalCount} entries`
                }
              </Typography>
            </Box>

            {/* Navigation Controls */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(1)}
              >
                <FirstPage />
              </IconButton>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                <NavigateBefore />
              </IconButton>

              {/* Page Numbers */}
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }

                if (page < 1 || page > totalPages) return null;

                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outlined'}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </Button>
                );
              })}

              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === totalPages || pageSize === -1}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                <NavigateNext />
              </IconButton>
              <IconButton
                size="sm"
                variant="outlined"
                disabled={currentPage === totalPages || pageSize === -1}
                onClick={() => setCurrentPage(totalPages)}
              >
                <LastPage />
              </IconButton>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};
